import type { EntitlementMap, EntitlementState, ProductId } from "@/api/types";
import type { AppId } from "@/types";

/**
 * Frontend entitlement helpers. The MAP ITSELF always comes from the session
 * bootstrap (`/api/auth/me` → useHive.entitlements) — nothing here hardcodes
 * who gets what; this file only interprets states and carries marketing copy
 * for the preview/locked surfaces.
 */

export const PRODUCT_IDS: ProductId[] = ["ares", "garlic", "athena", "byonc", "appgraph"];

export function isProductApp(appId: AppId): appId is ProductId {
  return (PRODUCT_IDS as string[]).includes(appId);
}

/** State for a product; hidden when the map is missing (no org context). */
export function productState(map: EntitlementMap | null, product: ProductId): EntitlementState {
  return map?.[product] ?? "hidden";
}

/** State for any app id: non-product (system) apps are always "enabled". */
export function appState(map: EntitlementMap | null, appId: AppId): EntitlementState {
  return isProductApp(appId) ? productState(map, appId) : "enabled";
}

export function isEnabled(map: EntitlementMap | null, product: ProductId): boolean {
  return productState(map, product) === "enabled";
}

/** Visible on tiles/dock (everything except hidden). */
export function isVisible(map: EntitlementMap | null, appId: AppId): boolean {
  return appState(map, appId) !== "hidden";
}

/** Gated = visible but not usable (preview or locked). */
export function isGated(map: EntitlementMap | null, appId: AppId): boolean {
  const s = appState(map, appId);
  return s === "preview" || s === "locked";
}

/** Product owning a workflow node (from the catalog's `integration` field —
 *  the same mapping the server enforces in entitlements/nodeProducts.ts). */
export function nodeProductOf(integration: string | undefined): ProductId | null {
  return integration && (PRODUCT_IDS as string[]).includes(integration)
    ? (integration as ProductId)
    : null;
}

/** Distinct non-enabled products among a workflow's active steps. */
export function gatedProductsIn(
  nodes: Array<{ integration?: string; disabled?: boolean }>,
  map: EntitlementMap | null,
): ProductId[] {
  const out = new Set<ProductId>();
  for (const n of nodes) {
    if (n.disabled) continue;
    const p = nodeProductOf(n.integration);
    if (p && productState(map, p) !== "enabled") out.add(p);
  }
  return [...out];
}

/** Capability blurbs for the preview modal / panel (copy only, not gating). */
export const PRODUCT_PREVIEW: Record<
  ProductId,
  { headline: string; capabilities: string[] }
> = {
  ares: {
    headline: "Verification-first AI development — agents that prove their work before it ships.",
    capabilities: [
      "Autonomous implementation agents that pick up refined Hive tasks",
      "Every change verified: builds, tests, and regression evidence attached",
      "Deep workspace integration — branches, snapshots, and runners managed for you",
    ],
  },
  garlic: {
    headline: "Agent specialization — tune expert agents on your stack, your conventions, your domain.",
    capabilities: [
      "Specializes agents per repository for sharper, safer output",
      "Continuous refresh keeps specializations current as your code evolves",
      "Plugs into workflows so specialized agents run your automations",
    ],
  },
  athena: {
    headline: "API readiness platform — contracts, drift, and readiness gates in one place.",
    capabilities: [
      "Continuous API readiness checks against real traffic shapes",
      "Drift detection between spec, implementation, and consumers",
      "Readiness gates for launches wired into your workflows",
    ],
  },
  byonc: {
    headline: "Hybrid inference routing — the right model, the right place, the right cost.",
    capabilities: [
      "Routes inference across providers and your own capacity",
      "Cost guardrails with automatic fallback and budget alerts",
      "Per-workspace routing policies (cost-optimized, latency-first, pinned)",
    ],
  },
  appgraph: {
    headline: "Visualize app dependencies — see every service, integration, and data flow.",
    capabilities: [
      "Live dependency graph of your apps, integrations, and workflows",
      "Impact analysis: what breaks if this service changes?",
      "Shared maps your whole org can explore",
    ],
  },
};
